import { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { apiHelpers } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import EstimateTemplateSelector from '@/components/EstimateTemplateSelector';
import QuickFieldNotes from '@/components/QuickFieldNotes';
import { 
  ArrowLeft, 
  ArrowRight, 
  Check, 
  Plus, 
  Trash2,
  User,
  MapPin,
  DollarSign,
  FileText,
  Save,
  Percent,
  Sparkles,
  StickyNote,
  ClipboardList,
  List,
  Eye,
  X,
  Star,
  Layers
} from 'lucide-react';

// Streamlined steps - Site Visit handles discovery, Estimates handle pricing
const ALL_STEPS = [
  { id: 1, title: 'Client', icon: User },
  { id: 2, title: 'Job Details', icon: MapPin },
  { id: 3, title: 'Line Items', icon: DollarSign },
  { id: 4, title: 'Review', icon: FileText }
];

// Helper to get steps based on line_items_mode setting
const getSteps = (lineItemsMode) => {
  if (lineItemsMode === 'hidden') {
    // Remove the Line Items step (id: 3) when hidden
    return ALL_STEPS.filter(s => s.id !== 3);
  }
  return ALL_STEPS;
};

// Pre-made Scope of Work templates by service type
const SCOPE_TEMPLATES = {
  'Concrete': `• Remove and dispose of existing concrete (if applicable)
• Excavate and grade area to proper depth
• Install compacted gravel base
• Set forms and install rebar/wire mesh reinforcement
• Pour concrete to specified thickness
• Finish surface (broom, stamped, or smooth per spec)
• Apply curing compound
• Clean up job site and haul away debris`,

  'Decking': `• Remove existing deck structure (if applicable)
• Install new pressure-treated posts with concrete footings
• Frame deck with treated lumber joists
• Install composite/wood decking boards
• Install railing system per code
• Add stairs with handrails (if needed)
• Final inspection and cleanup`,

  'Fencing': `• Mark property lines and call 811 for utilities
• Remove old fence (if applicable)
• Dig post holes to proper depth
• Set posts in concrete
• Install rails and fence boards/panels
• Install gate(s) with hardware
• Clean up and haul away debris`,

  'Landscaping': `• Site assessment and design review
• Clear existing vegetation as needed
• Grade and prepare soil
• Install irrigation system
• Plant trees, shrubs, and flowers per plan
• Apply mulch and ground cover
• Install landscape lighting (if included)
• Final walkthrough and maintenance instructions`,

  'Painting': `• Pressure wash exterior surfaces (if exterior)
• Scrape and sand loose/peeling paint
• Caulk gaps and fill holes
• Apply primer to bare surfaces
• Apply two coats of premium paint
• Paint trim and detail work
• Protect floors and furniture
• Clean up and final touch-ups`,

  'Roofing': `• Remove existing shingles and underlayment
• Inspect and repair decking as needed
• Install ice/water shield at eaves and valleys
• Install synthetic underlayment
• Install drip edge and flashing
• Install new shingles per manufacturer specs
• Install ridge vent and cap
• Clean up and haul away debris`,

  'Plumbing': `• Assess existing plumbing system
• Obtain necessary permits
• Install/repair fixtures as specified
• Test all connections for leaks
• Ensure proper drainage and venting
• Final inspection and code compliance
• Clean up work area`,

  'Electrical': `• Assess existing electrical system
• Obtain necessary permits
• Install new circuits/outlets as specified
• Install fixtures and switches
• Test all connections
• Final inspection and code compliance
• Clean up work area`,

  'HVAC': `• Assess existing HVAC system
• Remove old equipment (if replacing)
• Install new unit per manufacturer specs
• Connect ductwork and refrigerant lines
• Install thermostat and controls
• Test system operation
• Final inspection and cleanup`,

  'Patio': `• Mark and excavate area to proper depth
• Install compacted gravel base
• Set border/edge restraints
• Install pavers/stone per pattern
• Fill joints with polymeric sand
• Compact and seal surface
• Clean up and final walkthrough`,

  'General Construction': `• Review plans and obtain permits
• Site preparation and layout
• Complete work per specifications
• Quality control inspections
• Final cleanup and walkthrough
• Provide warranty documentation`,

  'Cleaning': `• Initial walkthrough and assessment
• Dust all surfaces from top to bottom
• Clean all windows and mirrors
• Vacuum carpets and mop hard floors
• Clean and sanitize bathrooms
• Clean and sanitize kitchen
• Final inspection and touch-ups`,

  'Flooring': `• Remove existing flooring
• Prepare and level subfloor
• Install moisture barrier (if needed)
• Install new flooring material
• Install transitions and trim
• Final cleaning and inspection`,

  'Other': `• Review project requirements
• Prepare work area
• Complete work per agreed specifications
• Quality inspection
• Clean up and final walkthrough`
};

const EstimateWizardPage = () => {
  const { organization, user, refreshSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { estimateId } = useParams(); // For edit mode
  const [searchParams] = useSearchParams();
  const isEditMode = Boolean(estimateId);
  
  // Pre-select client from URL params or location state
  const preSelectedClientId = searchParams.get('client_id') || location.state?.clientId || '';
  
  // Get the line items mode from org preferences (defaults to 'optional')
  const orgLineItemsMode = organization?.preferences?.line_items_mode || 'optional';
  
  // Local override for quick toggle (persists during this estimate creation)
  const [localLineItemsMode, setLocalLineItemsMode] = useState(null);
  const lineItemsMode = localLineItemsMode || orgLineItemsMode;
  
  // Get dynamic steps based on line_items_mode setting
  const STEPS = getSteps(lineItemsMode);
  // Review step (id=4) is always the final step
  const maxStep = 4;
  
  const [step, setStep] = useState(1);
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showQuickNotes, setShowQuickNotes] = useState(false);
  const [quickNotes, setQuickNotes] = useState({ notes: '', photos: [], measurements: [] });
  // Don't show template selector in edit mode or when client is pre-selected
  const [showTemplateSelector, setShowTemplateSelector] = useState(!isEditMode && !preSelectedClientId);
  const [showPreview, setShowPreview] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [linkedSiteVisit, setLinkedSiteVisit] = useState(null);
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  
  // Quick toggle handler for line items mode
  const handleQuickToggleLineItems = () => {
    // Toggle between 'optional' and 'hidden' for quick access
    const newMode = lineItemsMode === 'hidden' ? 'optional' : 'hidden';
    setLocalLineItemsMode(newMode);
    
    // If we're on step 3 (line items) and switching to hidden, go to step 4
    if (step === 3 && newMode === 'hidden') {
      setStep(4);
    }
    
    toast.success(
      newMode === 'hidden' 
        ? 'Line items step hidden - pricing can be added later' 
        : 'Line items step enabled',
      { duration: 2000 }
    );
  };
  
  // Form state - focused on pricing proposal
  const [formData, setFormData] = useState({
    client_id: preSelectedClientId,
    service_type: '',
    project_title: '',  // Optional custom title
    work_types: [],     // Multi-select work type tags
    job_address: '',
    requested_start_date: '',
    estimated_duration: '',  // e.g., "5 to 6 days"
    scope_of_work: '',
    line_items: [
      { name: '', description: '', qty: 1, unit: 'each', rate: 0 }
    ],
    summary_sections: [
      { title: '', description: '' }
    ],
    estimate_sections: [
      { id: crypto.randomUUID(), title: '', description: '', line_items: [{ name: '', description: '', qty: 1, unit: 'each', rate: 0 }], subtotal: 0, is_approved: false, order: 0 }
    ],
    summary_total: 0,  // Single total for summary mode
    // Simple mode fields
    simple_materials: '',
    simple_process: '',
    simple_pricing: [{ id: crypto.randomUUID(), title: '', price: 0 }],
    internal_notes: '',
    tax_rate: organization?.default_tax_percent || 8.25,
    discount_amount: 0,
    discount_percent: 0,
    require_deposit: true,
    deposit_type: 'percent',
    deposit_value: organization?.default_deposit_percent || 30,
    valid_days: 30,
    pdf_include_photos: true,
    pdf_include_sketches: true,
    display_mode: (organization?.enabled_estimate_modes?.[0]) || 'simple'  // Use first enabled mode, default to 'simple'
  });
  
  // Work type options per service type (niche-specific)
  const WORK_TYPE_OPTIONS = {
    'Concrete': ['Driveway', 'Patio', 'Sidewalk', 'Retaining Wall', 'Foundation', 'Slab', 'Stamped Concrete', 'Colored Concrete', 'Repair/Resurface', 'Steps/Stairs', 'Pool Deck', 'Garage Floor'],
    'Landscaping': ['Lawn Installation', 'Planting/Garden Beds', 'Mulching', 'Tree/Shrub Removal', 'Irrigation', 'Drainage', 'Grading', 'Sod Installation', 'Hardscape', 'Fence Installation', 'Outdoor Lighting', 'Water Feature'],
    'Fencing': ['Wood Fence', 'Vinyl Fence', 'Chain Link', 'Aluminum/Metal', 'Privacy Fence', 'Picket Fence', 'Gate Installation', 'Fence Repair', 'Post Replacement', 'Staining/Sealing'],
    'Roofing': ['Shingle Replacement', 'Metal Roofing', 'Flat Roof', 'Tile Roofing', 'Roof Repair', 'Gutter Installation', 'Skylight', 'Ventilation', 'Flashing', 'Roof Inspection'],
    'Painting': ['Interior Walls', 'Exterior Walls', 'Trim/Molding', 'Ceiling', 'Cabinet Painting', 'Deck/Fence Staining', 'Pressure Washing', 'Drywall Repair', 'Wallpaper Removal', 'Texture Coating'],
    'Electrical': ['Panel Upgrade', 'Outlet Installation', 'Light Fixture', 'Ceiling Fan', 'Wiring', 'EV Charger', 'Generator', 'Smart Home', 'Security System', 'Troubleshooting'],
    'Plumbing': ['Pipe Repair', 'Water Heater', 'Fixture Installation', 'Drain Cleaning', 'Sewer Line', 'Water Line', 'Garbage Disposal', 'Toilet Installation', 'Sump Pump', 'Gas Line'],
    'HVAC': ['AC Installation', 'Furnace Installation', 'Ductwork', 'Maintenance', 'Repair', 'Thermostat', 'Mini Split', 'Ventilation', 'Air Quality', 'Insulation'],
    'General': ['Demolition', 'Cleanup', 'Hauling', 'Site Prep', 'Permit Work', 'Inspection Ready', 'Custom Work', 'Consultation', 'Maintenance', 'Emergency Repair']
  };
  
  // Get available work types based on selected service type(s)
  const getAvailableWorkTypes = () => {
    if (!formData.service_type) return [];
    // Service type could be comma-separated for multiple
    const types = formData.service_type.split(',').map(t => t.trim());
    const allOptions = new Set();
    types.forEach(type => {
      (WORK_TYPE_OPTIONS[type] || WORK_TYPE_OPTIONS['General'] || []).forEach(opt => allOptions.add(opt));
    });
    return Array.from(allOptions).sort();
  };
  
  // New client form
  const [showNewClient, setShowNewClient] = useState(false);
  const [newClient, setNewClient] = useState({ name: '', email: '', phone: '', address: '' });

  // Handle template selection
  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    
    // Get enabled modes - use template's mode ONLY if it's enabled, otherwise use first enabled mode
    const enabledModes = organization?.enabled_estimate_modes || ['simple', 'detailed'];
    const templateMode = template.display_mode || 'detailed';
    const effectiveMode = enabledModes.includes(templateMode) ? templateMode : enabledModes[0];
    
    // Convert template data to match the effective mode
    let simpleData = {};
    if (effectiveMode === 'simple') {
      // If simple mode, convert template data to simple format
      // Materials from scope_of_work or line items
      const materials = template.line_items?.map(item => `• ${item.name || item.description}`).join('\n') || '';
      // Process from scope_of_work
      const process = template.default_scope_of_work || '';
      // Pricing from line items or summary sections
      const pricing = template.line_items?.length > 0
        ? template.line_items.map(item => ({
            id: crypto.randomUUID(),
            title: item.name || item.description || 'Work Item',
            price: (item.qty || item.quantity || 1) * (item.unit_price || item.rate || 0)
          }))
        : template.summary_sections?.length > 0
          ? template.summary_sections.map(section => ({
              id: crypto.randomUUID(),
              title: section.title || 'Work Item',
              price: section.price || 0
            }))
          : [{ id: crypto.randomUUID(), title: '', price: 0 }];
      
      simpleData = {
        simple_materials: materials,
        simple_process: process,
        simple_pricing: pricing
      };
    }
    
    setFormData(prev => ({
      ...prev,
      service_type: template.service_type || prev.service_type,
      scope_of_work: template.default_scope_of_work || prev.scope_of_work,
      display_mode: effectiveMode,  // Use effective mode, not template's mode
      summary_sections: template.summary_sections?.length > 0
        ? template.summary_sections.map(section => ({
            title: section.title || '',
            description: section.description || '',
            price: section.price || 0
          }))
        : prev.summary_sections,
      line_items: template.line_items?.length > 0 
        ? template.line_items.map(item => ({
            name: item.name || item.description || '',
            description: item.description || '',
            qty: item.quantity || item.qty || 1,
            unit: item.unit || 'each',
            rate: item.unit_price || item.rate || 0
          }))
        : prev.line_items,
      ...simpleData,  // Spread simple mode data if applicable
      tax_rate: template.default_tax_rate ?? prev.tax_rate,
      deposit_type: template.default_deposit_type || prev.deposit_type,
      deposit_value: template.default_deposit_value || prev.deposit_value,
      require_deposit: template.default_deposit_value > 0,
      valid_days: template.default_valid_days || prev.valid_days
    }));
    setShowTemplateSelector(false);
    toast.success(`Template "${template.name}" applied`);
  };

  useEffect(() => {
    loadClients();
  }, [organization?.id]);

  const loadClients = async () => {
    if (!organization?.id) return;
    
    try {
      const response = await apiHelpers.getClients(organization.id);
      setClients(response.data);
    } catch (error) {
      console.error('Failed to load clients:', error);
    }
  };
  
  // Load existing estimate for edit mode
  useEffect(() => {
    if (isEditMode && organization?.id && estimateId) {
      loadExistingEstimate();
    }
  }, [isEditMode, organization?.id, estimateId]);
  
  const loadExistingEstimate = async () => {
    setIsLoading(true);
    try {
      const response = await apiHelpers.getEstimate(organization.id, estimateId);
      const estimate = response.data;
      
      // Populate form with existing estimate data
      setFormData({
        client_id: estimate.client_id || '',
        service_type: estimate.service_type || '',
        job_address: estimate.job_address || '',
        requested_start_date: estimate.requested_start_date || '',
        estimated_duration: estimate.estimated_duration || '',
        scope_of_work: estimate.scope_of_work || '',
        project_title: estimate.project_title || '',
        work_types: estimate.work_types || [],
        line_items: estimate.line_items?.length > 0 
          ? estimate.line_items.map(item => ({
              name: item.name || '',
              description: item.description || '',
              qty: item.qty || item.quantity || 1,
              unit: item.unit || 'each',
              rate: item.rate || item.unit_price || 0
            }))
          : [{ name: '', description: '', qty: 1, unit: 'each', rate: 0 }],
        summary_sections: estimate.summary_sections?.length > 0
          ? estimate.summary_sections.map(section => ({
              title: section.title || '',
              description: section.description || ''
            }))
          : [{ title: '', description: '' }],
        summary_total: estimate.summary_total || estimate.total || 0,
        simple_materials: estimate.simple_materials || '',
        simple_process: estimate.simple_process || '',
        simple_pricing: estimate.simple_pricing?.length > 0 
          ? estimate.simple_pricing 
          : [{ id: crypto.randomUUID(), title: '', price: 0 }],
        internal_notes: estimate.internal_notes || '',
        tax_rate: estimate.tax_rate ?? organization?.default_tax_percent ?? 8.25,
        discount_amount: estimate.discount_amount || 0,
        discount_percent: estimate.discount_percent || 0,
        require_deposit: estimate.require_deposit ?? true,
        deposit_type: estimate.deposit_type || 'percent',
        deposit_value: estimate.deposit_value || organization?.default_deposit_percent || 30,
        valid_days: estimate.valid_days || 30,
        pdf_include_photos: estimate.pdf_include_photos ?? true,
        pdf_include_sketches: estimate.pdf_include_sketches ?? true,
        display_mode: estimate.display_mode || 'detailed'
      });
      
      // Load quick notes if they exist
      if (estimate.quick_notes) {
        setQuickNotes(estimate.quick_notes);
      }
      
      // Don't show template selector in edit mode
      setShowTemplateSelector(false);
      
      toast.success('Estimate loaded for editing');
    } catch (error) {
      console.error('Failed to load estimate:', error);
      toast.error('Failed to load estimate');
      navigate('/estimates');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Load linked site visit data if estimate was converted from one
  useEffect(() => {
    const siteVisitId = location.state?.fromSiteVisitId;
    if (siteVisitId && organization?.id) {
      loadLinkedSiteVisit(siteVisitId);
    }
  }, [location.state?.fromSiteVisitId, organization?.id]);

  // Auto-populate address when client is pre-selected from URL/state
  useEffect(() => {
    if (preSelectedClientId && clients.length > 0 && !formData.job_address) {
      const client = clients.find(c => c.id === preSelectedClientId);
      if (client?.address) {
        setFormData(prev => ({
          ...prev,
          job_address: client.address
        }));
      }
    }
  }, [preSelectedClientId, clients, formData.job_address]);

  const loadLinkedSiteVisit = async (visitId) => {
    try {
      const response = await apiHelpers.getSiteVisit(organization.id, visitId);
      setLinkedSiteVisit(response.data);
      // Pre-fill data from site visit
      if (response.data) {
        setFormData(prev => ({
          ...prev,
          client_id: response.data.client_id || prev.client_id,
          service_type: response.data.service_type || prev.service_type,
          job_address: response.data.job_address || prev.job_address,
          scope_of_work: response.data.project_notes || prev.scope_of_work
        }));
        // Also set quick notes from site visit photos/measurements
        if (response.data.photos?.length > 0 || response.data.structured_measurements?.length > 0) {
          setQuickNotes({
            notes: response.data.internal_notes || '',
            photos: response.data.photos || [],
            measurements: response.data.structured_measurements || []
          });
        }
      }
    } catch (error) {
      console.error('Failed to load linked site visit:', error);
    }
  };
  
  // Check if quick notes have content
  const hasNotesContent = quickNotes?.notes?.trim() || 
    quickNotes?.photos?.length > 0 || 
    quickNotes?.measurements?.length > 0;

  const updateForm = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Special handler for client selection that auto-populates address
  const selectClient = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    setFormData(prev => ({
      ...prev,
      client_id: clientId,
      // Auto-populate address if not already set
      job_address: prev.job_address || client?.address || ''
    }));
  };

  const updateLineItem = (index, field, value) => {
    setFormData(prev => {
      const items = [...prev.line_items];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, line_items: items };
    });
  };

  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      line_items: [...prev.line_items, { name: '', description: '', qty: 1, unit: 'each', rate: 0 }]
    }));
  };

  const removeLineItem = (index) => {
    if (formData.line_items.length === 1) return;
    setFormData(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index)
    }));
  };

  // Summary section helpers
  const updateSummarySection = (index, field, value) => {
    setFormData(prev => {
      const sections = [...(prev.summary_sections || [])];
      sections[index] = { ...sections[index], [field]: value };
      return { ...prev, summary_sections: sections };
    });
  };

  const addSummarySection = () => {
    setFormData(prev => ({
      ...prev,
      summary_sections: [...(prev.summary_sections || []), { title: '', description: '' }]
    }));
  };

  const removeSummarySection = (index) => {
    if ((formData.summary_sections || []).length <= 1) return;
    setFormData(prev => ({
      ...prev,
      summary_sections: (prev.summary_sections || []).filter((_, i) => i !== index)
    }));
  };

  // Estimate section helpers (for combined estimates)
  const addEstimateSection = () => {
    setFormData(prev => ({
      ...prev,
      estimate_sections: [
        ...(prev.estimate_sections || []), 
        { 
          id: crypto.randomUUID(), 
          title: '', 
          description: '', 
          line_items: [{ name: '', description: '', qty: 1, unit: 'each', rate: 0 }], 
          subtotal: 0, 
          is_approved: false, 
          order: (prev.estimate_sections || []).length 
        }
      ]
    }));
  };

  const removeEstimateSection = (sectionIndex) => {
    if ((formData.estimate_sections || []).length <= 1) return;
    setFormData(prev => ({
      ...prev,
      estimate_sections: (prev.estimate_sections || []).filter((_, i) => i !== sectionIndex)
    }));
  };

  const updateEstimateSection = (sectionIndex, field, value) => {
    setFormData(prev => {
      const sections = [...(prev.estimate_sections || [])];
      sections[sectionIndex] = { ...sections[sectionIndex], [field]: value };
      return { ...prev, estimate_sections: sections };
    });
  };

  const updateEstimateSectionLineItem = (sectionIndex, itemIndex, field, value) => {
    setFormData(prev => {
      const sections = [...(prev.estimate_sections || [])];
      const lineItems = [...(sections[sectionIndex].line_items || [])];
      lineItems[itemIndex] = { ...lineItems[itemIndex], [field]: value };
      
      // Recalculate section subtotal
      const subtotal = lineItems.reduce((sum, item) => {
        return sum + (parseFloat(item.qty) || 0) * (parseFloat(item.rate) || 0);
      }, 0);
      
      sections[sectionIndex] = { ...sections[sectionIndex], line_items: lineItems, subtotal };
      return { ...prev, estimate_sections: sections };
    });
  };

  const addLineItemToSection = (sectionIndex) => {
    setFormData(prev => {
      const sections = [...(prev.estimate_sections || [])];
      sections[sectionIndex] = {
        ...sections[sectionIndex],
        line_items: [...(sections[sectionIndex].line_items || []), { name: '', description: '', qty: 1, unit: 'each', rate: 0 }]
      };
      return { ...prev, estimate_sections: sections };
    });
  };

  const removeLineItemFromSection = (sectionIndex, itemIndex) => {
    if ((formData.estimate_sections?.[sectionIndex]?.line_items || []).length <= 1) return;
    setFormData(prev => {
      const sections = [...(prev.estimate_sections || [])];
      const lineItems = sections[sectionIndex].line_items.filter((_, i) => i !== itemIndex);
      
      // Recalculate section subtotal
      const subtotal = lineItems.reduce((sum, item) => {
        return sum + (parseFloat(item.qty) || 0) * (parseFloat(item.rate) || 0);
      }, 0);
      
      sections[sectionIndex] = { ...sections[sectionIndex], line_items: lineItems, subtotal };
      return { ...prev, estimate_sections: sections };
    });
  };

  const handleCreateClient = async () => {
    if (!newClient.name.trim()) {
      toast.error('Please enter a client name');
      return;
    }
    
    try {
      const response = await apiHelpers.createClient(
        organization.id,
        newClient,
        user.id,
        user.name
      );
      
      setClients(prev => [response.data, ...prev]);
      updateForm('client_id', response.data.id);
      setShowNewClient(false);
      setNewClient({ name: '', email: '', phone: '', address: '' });
      toast.success('Client added!');
    } catch (error) {
      console.error('Failed to create client:', error);
      toast.error('Failed to add client');
    }
  };

  const calculateTotals = () => {
    let subtotal = 0;
    
    if (formData.display_mode === 'summary') {
      // For summary mode, use the single summary_total
      subtotal = parseFloat(formData.summary_total) || 0;
    } else if (formData.display_mode === 'sections') {
      // For combined estimates, sum all section subtotals
      subtotal = (formData.estimate_sections || []).reduce((sum, section) => {
        return sum + (parseFloat(section.subtotal) || 0);
      }, 0);
    } else if (formData.display_mode === 'simple') {
      // For simple mode, sum all pricing options
      subtotal = (formData.simple_pricing || []).reduce((sum, item) => {
        return sum + (parseFloat(item.price) || 0);
      }, 0);
    } else {
      // Calculate from line items (with null safety)
      subtotal = (formData.line_items || []).reduce((sum, item) => {
        return sum + (parseFloat(item.qty) || 0) * (parseFloat(item.rate) || 0);
      }, 0);
    }
    
    const taxAmount = subtotal * (parseFloat(formData.tax_rate) || 0) / 100;
    const total = subtotal + taxAmount;
    
    // Calculate deposit amount
    let depositAmount = 0;
    if (formData.require_deposit) {
      if (formData.deposit_type === 'percent') {
        depositAmount = total * (parseFloat(formData.deposit_value) || 0) / 100;
      } else {
        depositAmount = parseFloat(formData.deposit_value) || 0;
      }
    }
    
    return { subtotal, taxAmount, total, depositAmount };
  };

  const validateStep = () => {
    switch (step) {
      case 1:
        if (!formData.client_id) {
          toast.error('Please select a client');
          return false;
        }
        return true;
      case 2:
        if (!formData.service_type) {
          toast.error('Please select a service type');
          return false;
        }
        return true;
      case 3:
        // Line items step - only shown when mode is 'required' or 'optional'
        if (lineItemsMode === 'required') {
          const hasValidItem = formData.line_items.some(item => item.name && item.rate > 0);
          if (!hasValidItem) {
            toast.error('Please add at least one line item');
            return false;
          }
        }
        // For 'optional' mode, no validation required - can proceed without line items
        return true;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep()) {
      // When line_items_mode is 'hidden', skip from step 2 directly to step 4 (review)
      if (step === 2 && lineItemsMode === 'hidden') {
        setStep(4);
      } else {
        setStep(prev => prev + 1);
      }
    }
  };

  const handleBack = () => {
    // When line_items_mode is 'hidden', go from step 4 (review) back to step 2 (job details)
    if (step === 4 && lineItemsMode === 'hidden') {
      setStep(2);
    } else {
      setStep(prev => prev - 1);
    };
  };

  const handleSaveDraft = async () => {
    if (!formData.client_id || !formData.service_type) {
      toast.error('Please fill in client and service type first');
      return;
    }
    
    setIsSaving(true);
    try {
      const estimateData = {
        ...formData,
        line_items: formData.line_items.filter(item => item.name && item.rate > 0),
        estimate_sections: (formData.estimate_sections || [])
          .filter(s => s.title.trim())
          .map(s => ({
            ...s,
            line_items: (s.line_items || []).filter(item => item.name && item.rate > 0)
          })),
        simple_pricing: (formData.simple_pricing || []).filter(item => item.title && item.price > 0),
        photos: quickNotes?.photos || [],
        measurements: quickNotes?.measurements?.reduce((acc, m) => {
          if (m.label && m.value) acc[m.label] = m.value;
          return acc;
        }, {}) || {},
        from_site_visit_id: linkedSiteVisit?.id || null
      };
      
      let response;
      if (isEditMode) {
        // Update existing estimate
        response = await apiHelpers.updateEstimate(organization.id, estimateId, estimateData);
        toast.success('Estimate updated!');
      } else {
        // Create new estimate
        response = await apiHelpers.createEstimate(organization.id, estimateData, user.id, user.name);
        toast.success('Estimate saved as draft!');
      }
      
      navigate(`/estimates/${isEditMode ? estimateId : response.data.id}`);
    } catch (error) {
      console.error('Failed to save estimate:', error);
      toast.error('Failed to save estimate');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreate = async () => {
    if (!validateStep()) return;
    
    setIsSaving(true);
    try {
      const estimateData = {
        ...formData,
        line_items: formData.line_items.filter(item => item.name && item.rate > 0),
        estimate_sections: (formData.estimate_sections || [])
          .filter(s => s.title.trim())
          .map(s => ({
            ...s,
            line_items: (s.line_items || []).filter(item => item.name && item.rate > 0)
          })),
        simple_pricing: (formData.simple_pricing || []).filter(item => item.title && item.price > 0),
        photos: quickNotes?.photos || [],
        measurements: quickNotes?.measurements?.reduce((acc, m) => {
          if (m.label && m.value) acc[m.label] = m.value;
          return acc;
        }, {}) || {},
        from_site_visit_id: linkedSiteVisit?.id || null
      };
      
      let response;
      if (isEditMode) {
        // Update existing estimate
        response = await apiHelpers.updateEstimate(organization.id, estimateId, estimateData);
        // Revise mode: auto-send the revised estimate to the client
        const isReviseMode = searchParams.get('revise') === '1';
        if (isReviseMode) {
          try {
            await apiHelpers.sendEstimate(organization.id, estimateId, user?.id, user?.name);
            toast.success('Revised estimate sent to client!', {
              description: 'The client will receive the updated estimate via email.'
            });
          } catch (sendErr) {
            console.error('Failed to auto-send revised estimate:', sendErr);
            toast.warning('Estimate saved but failed to send. Please click "Resend" on the detail page.');
          }
        } else {
          toast.success('Estimate updated!');
        }
      } else {
        // Create new estimate
        response = await apiHelpers.createEstimate(organization.id, estimateData, user.id, user.name);
        toast.success('Estimate created!');
      }
      
      navigate(`/estimates/${isEditMode ? estimateId : response.data.id}`);
    } catch (error) {
      console.error('Failed to create estimate:', error);
      toast.error(`Failed to ${isEditMode ? 'update' : 'create'} estimate`);
    } finally {
      setIsSaving(false);
    }
  };

  // Save current estimate as a template
  const handleSaveAsTemplate = async () => {
    if (!templateName.trim()) {
      toast.error('Please enter a template name');
      return;
    }

    setIsSavingTemplate(true);
    try {
      const templateData = {
        name: templateName.trim(),
        description: templateDescription.trim() || null,
        service_type: formData.service_type,
        display_mode: formData.display_mode,
        summary_sections: formData.summary_sections?.filter(s => s.title.trim()).map(s => ({
          title: s.title,
          description: s.description || ''
        })) || [],
        line_items: formData.line_items?.filter(item => item.name).map(item => ({
          name: item.name,
          description: item.description || '',
          qty: item.qty || 1,
          rate: item.rate || 0
        })) || [],
        default_tax_rate: formData.tax_rate || 0,
        default_deposit_type: formData.deposit_type || 'percent',
        default_deposit_value: formData.deposit_value || 0
      };

      await apiHelpers.createEstimateTemplate(organization.id, templateData, user?.id);
      toast.success(`Template "${templateName}" saved!`);
      setShowSaveTemplateModal(false);
      setTemplateName('');
      setTemplateDescription('');
    } catch (error) {
      console.error('Failed to save template:', error);
      toast.error('Failed to save template');
    } finally {
      setIsSavingTemplate(false);
    }
  };

  const selectedClient = clients.find(c => c.id === formData.client_id);
  const { subtotal, taxAmount, total, depositAmount } = calculateTotals();

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6 animate-fade-in">
            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl md:text-3xl font-bold">
                Select Client
              </h2>
              <p className="text-muted-foreground text-lg mt-2">
                Who is this estimate for?
              </p>
            </div>
            
            {!showNewClient ? (
              <>
                <div className="space-y-3 max-h-[300px] overflow-y-auto">
                  {clients.map((client) => (
                    <button
                      key={client.id}
                      onClick={() => selectClient(client.id)}
                      className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                        formData.client_id === client.id
                          ? 'border-primary bg-primary/5'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid={`select-client-${client.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary font-bold text-lg">
                          {client.name.charAt(0)}
                        </div>
                        <div>
                          <p className="font-semibold">{client.name}</p>
                          {client.email && (
                            <p className="text-sm text-muted-foreground">{client.email}</p>
                          )}
                          {client.address && (
                            <p className="text-xs text-muted-foreground mt-1">{client.address}</p>
                          )}
                        </div>
                        {formData.client_id === client.id && (
                          <Check className="w-6 h-6 text-primary ml-auto" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
                
                <Button
                  variant="outline"
                  onClick={() => setShowNewClient(true)}
                  className="w-full h-14 text-lg"
                  data-testid="add-new-client-btn"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Add New Client
                </Button>
              </>
            ) : (
              <div className="space-y-4 p-6 border-2 border-dashed border-gray-200 rounded-xl">
                <h3 className="font-semibold text-lg">New Client</h3>
                <div>
                  <Label>Name *</Label>
                  <Input
                    value={newClient.name}
                    onChange={(e) => setNewClient(prev => ({ ...prev, name: e.target.value }))}
                    className="input-default mt-1"
                    placeholder="John Smith"
                    data-testid="new-client-name"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={newClient.email}
                      onChange={(e) => setNewClient(prev => ({ ...prev, email: e.target.value }))}
                      className="input-default mt-1"
                      placeholder="john@email.com"
                    />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={newClient.phone}
                      onChange={(e) => setNewClient(prev => ({ ...prev, phone: e.target.value }))}
                      className="input-default mt-1"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowNewClient(false)}
                    className="flex-1 h-12"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCreateClient}
                    className="flex-1 h-12 btn-primary"
                    data-testid="save-new-client-btn"
                  >
                    <Check className="w-5 h-5 mr-2" />
                    Add Client
                  </Button>
                </div>
              </div>
            )}
          </div>
        );
        
      case 2:
        return (
          <div className="space-y-6 animate-fade-in">
            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl md:text-3xl font-bold">
                Job Details
              </h2>
              <p className="text-muted-foreground text-lg mt-2">
                What work will you be doing?
              </p>
            </div>
            
            <div>
              <Label className="text-lg">Service Type *</Label>
              <Select
                value={formData.service_type}
                onValueChange={(value) => {
                  updateForm('service_type', value);
                  // Clear work_types when service type changes
                  updateForm('work_types', []);
                }}
              >
                <SelectTrigger className="input-default mt-2" data-testid="service-type-select">
                  <SelectValue placeholder="Select service type" />
                </SelectTrigger>
                <SelectContent>
                  {organization?.service_types_enabled?.map((service) => (
                    <SelectItem key={service} value={service}>
                      {service}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Project Title - Optional */}
            <div>
              <Label className="text-lg">Project Title <span className="text-sm font-normal text-muted-foreground">(optional)</span></Label>
              <Input
                value={formData.project_title}
                onChange={(e) => updateForm('project_title', e.target.value)}
                placeholder="e.g., Driveway & Patio Concrete Work"
                className="input-default mt-2"
                data-testid="project-title-input"
              />
              <p className="text-xs text-muted-foreground mt-1">Custom title shown on estimate - leave blank to use service type</p>
            </div>
            
            {/* Work Types - Multi-select tags */}
            {formData.service_type && getAvailableWorkTypes().length > 0 && (
              <div>
                <Label className="text-lg">Work Types <span className="text-sm font-normal text-muted-foreground">(optional)</span></Label>
                <p className="text-sm text-muted-foreground mb-2">Select all that apply - helps with reporting</p>
                <div className="flex flex-wrap gap-2 p-3 border rounded-xl bg-gray-50/50 max-h-48 overflow-y-auto">
                  {getAvailableWorkTypes().map((workType) => {
                    const isSelected = formData.work_types?.includes(workType);
                    return (
                      <button
                        key={workType}
                        type="button"
                        onClick={() => {
                          const current = formData.work_types || [];
                          const updated = isSelected 
                            ? current.filter(t => t !== workType)
                            : [...current, workType];
                          updateForm('work_types', updated);
                        }}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                          isSelected 
                            ? 'bg-primary text-primary-foreground shadow-sm' 
                            : 'bg-white border border-gray-200 text-gray-700 hover:border-primary hover:text-primary'
                        }`}
                        data-testid={`work-type-${workType.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {workType}
                      </button>
                    );
                  })}
                </div>
                {formData.work_types?.length > 0 && (
                  <p className="text-xs text-primary mt-2">
                    Selected: {formData.work_types.join(', ')}
                  </p>
                )}
              </div>
            )}
            
            <div>
              <Label className="text-lg">Job Address</Label>
              <Input
                value={formData.job_address}
                onChange={(e) => updateForm('job_address', e.target.value)}
                placeholder="123 Main St, City, State 12345"
                className="input-default mt-2"
                data-testid="job-address-input"
              />
              {selectedClient?.address && (
                <Button
                  variant="link"
                  onClick={() => updateForm('job_address', selectedClient.address)}
                  className="text-sm text-primary p-0 h-auto mt-1"
                >
                  Use client address: {selectedClient.address}
                </Button>
              )}
            </div>
            
            <div>
              <Label className="text-lg">Requested Start Date</Label>
              <Input
                type="date"
                value={formData.requested_start_date}
                onChange={(e) => updateForm('requested_start_date', e.target.value)}
                className="input-default mt-2"
                data-testid="start-date-input"
              />
            </div>
            
            <div>
              <Label className="text-lg">Estimated Duration</Label>
              <p className="text-sm text-muted-foreground mb-2">
                How long will the project take? (e.g., "5 to 6 days", "2 weeks")
              </p>
              <Input
                type="text"
                placeholder="e.g., 5 to 6 days"
                value={formData.estimated_duration}
                onChange={(e) => updateForm('estimated_duration', e.target.value)}
                className="input-default"
                data-testid="estimated-duration-input"
              />
            </div>
            
            {/* Scope of Work - HIDE for Simple mode since it has its own Materials/Process */}
            {formData.display_mode !== 'simple' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-lg">Scope of Work</Label>
                  <p className="text-sm text-muted-foreground">
                    Describe what will be done - this appears on the PDF
                  </p>
                </div>
              </div>
              
              {/* Template Selector */}
              {formData.service_type && SCOPE_TEMPLATES[formData.service_type] && (
                <div className="p-3 bg-blue-50 rounded-xl border border-blue-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-blue-800 font-medium">
                      Template available for {formData.service_type}
                    </span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => updateForm('scope_of_work', SCOPE_TEMPLATES[formData.service_type])}
                      className="text-blue-700 border-blue-300 hover:bg-blue-100"
                      data-testid="use-template-btn"
                    >
                      <FileText className="w-4 h-4 mr-1" />
                      Use Template
                    </Button>
                  </div>
                </div>
              )}
              
              <textarea
                value={formData.scope_of_work}
                onChange={(e) => updateForm('scope_of_work', e.target.value)}
                placeholder={formData.service_type && SCOPE_TEMPLATES[formData.service_type] 
                  ? `Click "Use Template" above to start with a ${formData.service_type} template, or write your own scope...`
                  : "• List the work to be completed\n• Include key steps\n• Be specific about materials\n• Note any cleanup included"}
                className="w-full h-48 p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none text-sm"
                data-testid="scope-of-work-input"
              />
              <p className="text-xs text-muted-foreground">
                Tip: Use bullet points (•) for better readability. You can edit the template after inserting it.
              </p>
            </div>
            )}
          </div>
        );
        
      case 3:
        // Line Items step - pricing focused with summary/detailed/combined toggle
        return (
          <div className="space-y-6 animate-fade-in">
            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl md:text-3xl font-bold">
                Pricing
              </h2>
              <p className="text-muted-foreground text-lg mt-2">
                {lineItemsMode === 'required' ? 'Add your services and costs' : 'Add pricing now or later'}
              </p>
            </div>
            
            {/* Display Mode Toggle - Only show enabled modes */}
            {(() => {
              const enabledModes = organization?.enabled_estimate_modes || ['simple', 'detailed'];
              const modeButtons = [
                { id: 'simple', label: 'Simple', icon: FileText },
                { id: 'summary', label: 'Summary', icon: List },
                { id: 'detailed', label: 'Itemized', icon: ClipboardList },
                { id: 'sections', label: 'Combined', icon: Layers }
              ].filter(m => enabledModes.includes(m.id));
              
              // If only one mode enabled, don't show toggle
              if (modeButtons.length <= 1) return null;
              
              return (
                <div className="flex items-center justify-center gap-1 p-1.5 bg-gray-100 rounded-xl flex-wrap">
                  {modeButtons.map(mode => (
                    <button
                      key={mode.id}
                      onClick={() => updateForm('display_mode', mode.id)}
                      className={`flex-1 min-w-[80px] py-2.5 px-3 rounded-lg font-medium transition-all text-sm ${
                        formData.display_mode === mode.id 
                          ? 'bg-white shadow-sm text-primary' 
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                      data-testid={`${mode.id}-mode-btn`}
                    >
                      <mode.icon className="w-4 h-4 inline mr-1.5" />
                      {mode.label}
                    </button>
                  ))}
                </div>
              );
            })()}
            
            {/* Simple Mode */}
            {formData.display_mode === 'simple' && (
              <div className="space-y-6">
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                  <p className="text-sm text-emerald-800">
                    <strong>Simple Mode:</strong> Show just the scope of work (materials + process) and pricing options. No quantities or units - clean and straightforward.
                  </p>
                </div>
                
                {/* Scope of Work Section */}
                <div className="border border-gray-200 rounded-xl p-5 space-y-4">
                  <h3 className="font-semibold text-lg flex items-center gap-2">
                    <ClipboardList className="w-5 h-5 text-primary" />
                    Scope of Work
                  </h3>
                  
                  {/* Materials */}
                  <div>
                    <Label className="mb-2 block font-medium">Materials</Label>
                    <p className="text-sm text-muted-foreground mb-2">List the materials that will be used (one per line)</p>
                    <Textarea
                      value={formData.simple_materials || ''}
                      onChange={(e) => updateForm('simple_materials', e.target.value)}
                      placeholder="• Rebar #4&#10;• Concrete&#10;• Wire mesh&#10;• Expansion joints"
                      rows={5}
                      className="font-mono text-sm"
                      data-testid="simple-materials-input"
                    />
                  </div>
                  
                  {/* Process */}
                  <div>
                    <Label className="mb-2 block font-medium">Process</Label>
                    <p className="text-sm text-muted-foreground mb-2">Describe the work steps (one per line)</p>
                    <Textarea
                      value={formData.simple_process || ''}
                      onChange={(e) => updateForm('simple_process', e.target.value)}
                      placeholder="• Dig out old concrete&#10;• Framing&#10;• Lay in rebar&#10;• Pour concrete&#10;• Stamp/finish&#10;• Sealer application"
                      rows={6}
                      className="font-mono text-sm"
                      data-testid="simple-process-input"
                    />
                  </div>
                </div>
                
                {/* Work Items / Pricing */}
                <div className="border border-gray-200 rounded-xl p-5 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-lg flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-primary" />
                      Work Items & Costs
                    </h3>
                    <button
                      type="button"
                      onClick={() => {
                        const newPricing = [...(formData.simple_pricing || []), { id: crypto.randomUUID(), title: '', price: 0 }];
                        updateForm('simple_pricing', newPricing);
                      }}
                      className="flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 font-medium"
                      data-testid="add-pricing-option-btn"
                    >
                      <Plus className="w-4 h-4" />
                      Add Item
                    </button>
                  </div>
                  <p className="text-sm text-muted-foreground">Add each work item with its cost (e.g., "Driveway - $4,000", "Patio - $2,500")</p>
                  
                  <div className="space-y-3">
                    {(formData.simple_pricing || []).map((item, index) => (
                      <div key={item.id || index} className="flex items-center gap-3 bg-gray-50 p-3 rounded-lg">
                        <div className="flex-1">
                          <Input
                            value={item.title || ''}
                            onChange={(e) => {
                              const newPricing = [...formData.simple_pricing];
                              newPricing[index] = { ...newPricing[index], title: e.target.value };
                              updateForm('simple_pricing', newPricing);
                            }}
                            placeholder="Work item (e.g., Driveway, Patio Extension)"
                            className="bg-white"
                            data-testid={`pricing-title-${index}`}
                          />
                        </div>
                        <div className="w-32">
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                            <Input
                              type="number"
                              value={item.price || ''}
                              onChange={(e) => {
                                const newPricing = [...formData.simple_pricing];
                                newPricing[index] = { ...newPricing[index], price: parseFloat(e.target.value) || 0 };
                                updateForm('simple_pricing', newPricing);
                              }}
                              placeholder="0.00"
                              className="bg-white pl-7"
                              data-testid={`pricing-price-${index}`}
                            />
                          </div>
                        </div>
                        {formData.simple_pricing.length > 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              const newPricing = formData.simple_pricing.filter((_, i) => i !== index);
                              updateForm('simple_pricing', newPricing);
                            }}
                            className="text-red-500 hover:text-red-700 p-1"
                            data-testid={`remove-pricing-${index}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {/* Total Display */}
                  <div className="pt-3 border-t border-gray-200">
                    <div className="flex justify-between items-center text-lg">
                      <span className="font-medium">Subtotal (all options):</span>
                      <span className="font-bold text-primary">
                        ${(formData.simple_pricing || []).reduce((sum, item) => sum + (item.price || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Client can approve all work or select specific items they want completed</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Summary Mode */}
            {formData.display_mode === 'summary' && (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Summary Mode:</strong> Add sections with scope/description. Enter the grand total at the bottom.
                  </p>
                </div>
                
                {(formData.summary_sections || []).map((section, index) => (
                  <div 
                    key={index} 
                    className="p-4 border border-gray-200 rounded-xl space-y-4"
                    data-testid={`summary-section-${index}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Section {index + 1}</span>
                      {(formData.summary_sections || []).length > 1 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeSummarySection(index)}
                          className="text-destructive"
                        >
                          <Trash2 className="w-5 h-5" />
                        </Button>
                      )}
                    </div>
                    
                    <div>
                      <Label>Title *</Label>
                      <Input
                        value={section.title}
                        onChange={(e) => updateSummarySection(index, 'title', e.target.value)}
                        placeholder="e.g., Slab & Steps, Driveway Work"
                        className="input-default mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label>Scope / Description</Label>
                      <textarea
                        value={section.description}
                        onChange={(e) => updateSummarySection(index, 'description', e.target.value)}
                        placeholder={"• Materials included\n• Process details\n• Timeline notes"}
                        className="w-full h-32 p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none text-sm mt-1"
                      />
                    </div>
                  </div>
                ))}
                
                <Button
                  variant="outline"
                  onClick={addSummarySection}
                  className="w-full h-12 text-base"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Add Another Section
                </Button>
                
                {/* Grand Total for Summary Mode */}
                <div className="p-4 bg-primary/5 border-2 border-primary/20 rounded-xl">
                  <Label className="text-lg font-semibold">Grand Total *</Label>
                  <p className="text-sm text-muted-foreground mb-3">Enter the total price for all work</p>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl font-bold text-gray-400">$</span>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.summary_total || ''}
                      onChange={(e) => updateForm('summary_total', parseFloat(e.target.value) || 0)}
                      placeholder="0.00"
                      className="input-default pl-10 h-14 text-2xl font-bold"
                      data-testid="summary-grand-total"
                    />
                  </div>
                </div>
              </div>
            )}
            
            {/* Detailed/Itemized Mode */}
            {formData.display_mode === 'detailed' && (
              <>
            {/* Optional notice - only show when mode is 'optional' */}
            {lineItemsMode === 'optional' && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
                <p className="text-sm text-blue-800">
                  <strong>Tip:</strong> Line items are optional at this stage. You can save the estimate now and add pricing later from the estimate detail page.
                </p>
              </div>
            )}
            
            {/* Required notice - only show when mode is 'required' */}
            {lineItemsMode === 'required' && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> At least one line item with pricing is required before you can proceed.
                </p>
              </div>
            )}
            
            <div className="space-y-4">
              {formData.line_items.map((item, index) => (
                <div 
                  key={index} 
                  className="p-4 border border-gray-200 rounded-xl space-y-4"
                  data-testid={`line-item-${index}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Item {index + 1}</span>
                    {formData.line_items.length > 1 && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeLineItem(index)}
                        className="text-destructive"
                      >
                        <Trash2 className="w-5 h-5" />
                      </Button>
                    )}
                  </div>
                  
                  <div>
                    <Label>Name *</Label>
                    <Input
                      value={item.name}
                      onChange={(e) => updateLineItem(index, 'name', e.target.value)}
                      placeholder="e.g., Labor, Materials, etc."
                      className="input-default mt-1"
                      data-testid={`line-item-${index}-name`}
                    />
                  </div>
                  
                  <div>
                    <Label>Description</Label>
                    <Input
                      value={item.description}
                      onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                      placeholder="Optional details"
                      className="input-default mt-1"
                    />
                  </div>
                  
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label>Qty</Label>
                      <Input
                        type="text"
                        inputMode="decimal"
                        value={item.qty === 0 ? '' : item.qty}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === '' || /^\d*\.?\d*$/.test(val)) {
                            updateLineItem(index, 'qty', val === '' ? '' : parseFloat(val) || 0);
                          }
                        }}
                        onBlur={(e) => {
                          const val = parseFloat(e.target.value);
                          updateLineItem(index, 'qty', val > 0 ? val : 1);
                        }}
                        placeholder="1"
                        className="input-default mt-1"
                        data-testid={`line-item-${index}-qty`}
                      />
                    </div>
                    <div>
                      <Label>Unit</Label>
                      <Select
                        value={item.unit}
                        onValueChange={(value) => updateLineItem(index, 'unit', value)}
                      >
                        <SelectTrigger className="input-default mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="each">Each</SelectItem>
                          <SelectItem value="hour">Hour</SelectItem>
                          <SelectItem value="sqft">Sq Ft</SelectItem>
                          <SelectItem value="linear_ft">Linear Ft</SelectItem>
                          <SelectItem value="day">Day</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Rate ($)</Label>
                      <Input
                        type="text"
                        inputMode="decimal"
                        value={item.rate === 0 ? '' : item.rate}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === '' || /^\d*\.?\d*$/.test(val)) {
                            updateLineItem(index, 'rate', val === '' ? '' : parseFloat(val) || 0);
                          }
                        }}
                        onBlur={(e) => {
                          const val = parseFloat(e.target.value);
                          updateLineItem(index, 'rate', val >= 0 ? val : 0);
                        }}
                        placeholder="0.00"
                        className="input-default mt-1"
                        data-testid={`line-item-${index}-rate`}
                      />
                    </div>
                  </div>
                  
                  <div className="text-right font-bold text-lg">
                    Total: ${((item.qty || 0) * (item.rate || 0)).toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
            
            <Button
              variant="outline"
              onClick={addLineItem}
              className="w-full h-14 text-lg"
              data-testid="add-line-item-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Another Item
            </Button>
              </>
            )}
            
            {/* Combined/Sections Mode */}
            {formData.display_mode === 'sections' && (
              <div className="space-y-6">
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                  <p className="text-sm text-purple-800">
                    <strong>Combined Estimate:</strong> Create multiple sections, each with its own line items and subtotal. 
                    Clients can approve individual sections or the entire estimate.
                  </p>
                </div>
                
                {(formData.estimate_sections || []).map((section, sectionIndex) => (
                  <div 
                    key={section.id} 
                    className="border-2 border-purple-200 rounded-xl overflow-hidden"
                    data-testid={`estimate-section-${sectionIndex}`}
                  >
                    {/* Section Header */}
                    <div className="bg-purple-100 p-4 flex items-center justify-between">
                      <div className="flex-1">
                        <Input
                          value={section.title}
                          onChange={(e) => updateEstimateSection(sectionIndex, 'title', e.target.value)}
                          placeholder={`Section ${sectionIndex + 1} Title (e.g., Driveway Replacement)`}
                          className="font-semibold text-lg border-0 bg-transparent p-0 focus-visible:ring-0 placeholder:text-purple-400"
                        />
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <span className="font-bold text-purple-700">
                          ${(section.subtotal || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </span>
                        {(formData.estimate_sections || []).length > 1 && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeEstimateSection(sectionIndex)}
                            className="text-destructive hover:bg-red-100"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    {/* Section Description */}
                    <div className="p-4 border-b border-purple-100">
                      <Label className="text-sm text-muted-foreground">Description / Scope</Label>
                      <Textarea
                        value={section.description || ''}
                        onChange={(e) => updateEstimateSection(sectionIndex, 'description', e.target.value)}
                        placeholder="Describe the work in this section..."
                        className="mt-1 min-h-[60px]"
                      />
                    </div>
                    
                    {/* Section Line Items */}
                    <div className="p-4 space-y-3">
                      {(section.line_items || []).map((item, itemIndex) => (
                        <div 
                          key={itemIndex}
                          className="grid grid-cols-12 gap-2 items-end p-2 bg-gray-50 rounded-lg"
                        >
                          <div className="col-span-4">
                            <Label className="text-xs">Item</Label>
                            <Input
                              value={item.name}
                              onChange={(e) => updateEstimateSectionLineItem(sectionIndex, itemIndex, 'name', e.target.value)}
                              placeholder="Item name"
                              className="h-9 text-sm"
                            />
                          </div>
                          <div className="col-span-2">
                            <Label className="text-xs">Qty</Label>
                            <Input
                              type="number"
                              min="0"
                              step="0.5"
                              value={item.qty}
                              onChange={(e) => updateEstimateSectionLineItem(sectionIndex, itemIndex, 'qty', parseFloat(e.target.value) || 0)}
                              className="h-9 text-sm"
                            />
                          </div>
                          <div className="col-span-2">
                            <Label className="text-xs">Unit</Label>
                            <Select
                              value={item.unit}
                              onValueChange={(value) => updateEstimateSectionLineItem(sectionIndex, itemIndex, 'unit', value)}
                            >
                              <SelectTrigger className="h-9 text-sm">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="each">Each</SelectItem>
                                <SelectItem value="hour">Hour</SelectItem>
                                <SelectItem value="sqft">Sq Ft</SelectItem>
                                <SelectItem value="linear_ft">Lin Ft</SelectItem>
                                <SelectItem value="day">Day</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="col-span-2">
                            <Label className="text-xs">Rate</Label>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.rate}
                              onChange={(e) => updateEstimateSectionLineItem(sectionIndex, itemIndex, 'rate', parseFloat(e.target.value) || 0)}
                              className="h-9 text-sm"
                            />
                          </div>
                          <div className="col-span-2 flex items-center gap-1">
                            <span className="text-sm font-medium flex-1 text-right">
                              ${((item.qty || 0) * (item.rate || 0)).toFixed(2)}
                            </span>
                            {(section.line_items || []).length > 1 && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive"
                                onClick={() => removeLineItemFromSection(sectionIndex, itemIndex)}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => addLineItemToSection(sectionIndex)}
                        className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Item to Section
                      </Button>
                    </div>
                  </div>
                ))}
                
                <Button
                  variant="outline"
                  onClick={addEstimateSection}
                  className="w-full h-14 text-lg border-purple-300 text-purple-700 hover:bg-purple-50"
                  data-testid="add-section-btn"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Add Another Section
                </Button>
              </div>
            )}
            
            {/* Tax Rate */}
            <div className="p-4 bg-gray-50 rounded-xl">
              <div className="flex items-center justify-between">
                <Label className="text-lg">Tax Rate (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={formData.tax_rate}
                  onChange={(e) => updateForm('tax_rate', parseFloat(e.target.value) || 0)}
                  className="input-default w-24 text-center"
                  data-testid="tax-rate-input"
                />
              </div>
            </div>
            
            {/* Deposit Section */}
            <div className="p-4 bg-amber-50 rounded-xl space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-lg flex items-center gap-2">
                    <Percent className="w-5 h-5" />
                    Require Deposit to Schedule
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    Customer must pay deposit before work begins
                  </p>
                </div>
                <Switch
                  checked={formData.require_deposit}
                  onCheckedChange={(checked) => updateForm('require_deposit', checked)}
                  data-testid="require-deposit-toggle"
                />
              </div>
              
              {formData.require_deposit && (
                <div className="grid grid-cols-2 gap-4 pt-2 border-t border-amber-200">
                  <div>
                    <Label>Deposit Type</Label>
                    <Select
                      value={formData.deposit_type}
                      onValueChange={(value) => updateForm('deposit_type', value)}
                    >
                      <SelectTrigger className="input-default mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percent">% of Total</SelectItem>
                        <SelectItem value="fixed">Fixed Amount</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>
                      {formData.deposit_type === 'percent' ? 'Deposit %' : 'Deposit Amount ($)'}
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      max={formData.deposit_type === 'percent' ? 100 : undefined}
                      step={formData.deposit_type === 'percent' ? 5 : 0.01}
                      value={formData.deposit_value}
                      onChange={(e) => updateForm('deposit_value', parseFloat(e.target.value) || 0)}
                      className="input-default mt-1"
                      data-testid="deposit-value-input"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* PDF Include Toggles */}
            <div className="p-4 rounded-xl border border-blue-200 bg-blue-50/50 space-y-3" data-testid="pdf-include-section">
              <div>
                <p className="font-semibold text-sm text-blue-900">PDF Output</p>
                <p className="text-xs text-blue-700/80 mt-0.5">Choose what gets attached to the customer-facing estimate PDF.</p>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Include damage / before photos</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">Show site condition photos in the PDF the customer receives.</p>
                </div>
                <Switch
                  checked={formData.pdf_include_photos}
                  onCheckedChange={(checked) => updateForm('pdf_include_photos', checked)}
                  data-testid="pdf-include-photos-toggle"
                />
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-blue-200">
                <div>
                  <Label className="text-sm font-medium">Include sketches / diagrams</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">Show drawn sketches in the PDF (useful for layouts and measurements).</p>
                </div>
                <Switch
                  checked={formData.pdf_include_sketches}
                  onCheckedChange={(checked) => updateForm('pdf_include_sketches', checked)}
                  data-testid="pdf-include-sketches-toggle"
                />
              </div>
            </div>

            {/* Totals */}
            <div className="p-4 bg-primary/5 rounded-xl space-y-2">
              <div className="flex justify-between text-lg">
                <span>Subtotal:</span>
                <span>${subtotal.toFixed(2)}</span>
              </div>
              {taxAmount > 0 && (
                <div className="flex justify-between text-lg">
                  <span>Tax ({formData.tax_rate}%):</span>
                  <span>${taxAmount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-2xl font-bold border-t border-primary/20 pt-2">
                <span>Total:</span>
                <span>${total.toFixed(2)}</span>
              </div>
              {formData.require_deposit && (
                <>
                  <div className="flex justify-between text-lg text-amber-700 font-medium pt-2 border-t border-primary/20">
                    <span>Deposit Due ({formData.deposit_type === 'percent' ? `${formData.deposit_value}%` : 'fixed'}):</span>
                    <span>
                      ${(formData.deposit_type === 'percent' 
                        ? (total * formData.deposit_value / 100) 
                        : formData.deposit_value).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-lg">
                    <span>Remaining Balance:</span>
                    <span>
                      ${(total - (formData.deposit_type === 'percent' 
                        ? (total * formData.deposit_value / 100) 
                        : formData.deposit_value)).toFixed(2)}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        );
        
      case 4:
        return (
          <div className="space-y-6 animate-fade-in">
            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl md:text-3xl font-bold">
                Review Estimate
              </h2>
              <p className="text-muted-foreground text-lg mt-2">
                Double-check everything looks good
              </p>
            </div>
            
            {/* Client */}
            <div className="card-highlight">
              <p className="text-sm text-muted-foreground mb-1">Client</p>
              <p className="font-semibold text-lg">{selectedClient?.name}</p>
              {selectedClient?.email && (
                <p className="text-muted-foreground">{selectedClient.email}</p>
              )}
            </div>
            
            {/* Job Details */}
            <div className="card-default">
              <h3 className="font-semibold text-lg mb-3">Job Details</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Service:</span>
                  <span className="font-medium">{formData.service_type}</span>
                </div>
                {formData.job_address && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Address:</span>
                    <span className="font-medium">{formData.job_address}</span>
                  </div>
                )}
                {formData.requested_start_date && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Start Date:</span>
                    <span className="font-medium">{formData.requested_start_date}</span>
                  </div>
                )}
                {formData.estimated_duration && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Est. Duration:</span>
                    <span className="font-medium">{formData.estimated_duration}</span>
                  </div>
                )}
              </div>
            </div>
            
            {/* SIMPLE MODE - Scope of Work + Work Items */}
            {formData.display_mode === 'simple' && (
              <>
                {/* Scope of Work */}
                {(formData.simple_materials || formData.simple_process) && (
                  <div className="card-default">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-lg">Scope of Work</h3>
                      <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-700">Simple</span>
                    </div>
                    {formData.simple_materials && (
                      <div className="mb-4">
                        <p className="text-sm font-medium text-gray-700 mb-1">Materials:</p>
                        <div className="bg-gray-50 p-3 rounded-lg text-sm whitespace-pre-line">
                          {formData.simple_materials}
                        </div>
                      </div>
                    )}
                    {formData.simple_process && (
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-1">Process:</p>
                        <div className="bg-gray-50 p-3 rounded-lg text-sm whitespace-pre-line">
                          {formData.simple_process}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Work Items */}
                <div className="card-default">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-lg">Cost Breakdown</h3>
                  </div>
                  <div className="space-y-3">
                    {(formData.simple_pricing || []).filter(item => item.title).map((item, index) => (
                      <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                        <span className="font-medium">{item.title}</span>
                        <span className="font-bold text-primary">${(item.price || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                      </div>
                    ))}
                    {(formData.simple_pricing || []).filter(item => item.title).length === 0 && (
                      <p className="text-muted-foreground text-center py-4">No work items added</p>
                    )}
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-gray-200 space-y-2">
                    <div className="flex justify-between">
                      <span>Subtotal:</span>
                      <span>${subtotal.toFixed(2)}</span>
                    </div>
                    {taxAmount > 0 && (
                      <div className="flex justify-between">
                        <span>Tax ({formData.tax_rate}%):</span>
                        <span>${taxAmount.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-xl font-bold">
                      <span>Total:</span>
                      <span className="text-primary">${total.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </>
            )}
            
            {/* SUMMARY MODE - Pricing Sections */}
            {formData.display_mode === 'summary' && (
              <div className="card-default">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-lg">Pricing Sections</h3>
                  <span className="text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-700">Summary</span>
                </div>
                <div className="space-y-3">
                  {(formData.summary_sections || []).filter(s => s.title).map((section, index) => (
                    <div key={index} className="border border-gray-200 rounded-xl overflow-hidden">
                      <div className="bg-primary/10 p-3">
                        <span className="font-semibold">{section.title}</span>
                      </div>
                      {section.description && (
                        <div className="p-3 text-sm text-muted-foreground whitespace-pre-line bg-gray-50">
                          {section.description}
                        </div>
                      )}
                    </div>
                  ))}
                  {(formData.summary_sections || []).filter(s => s.title).length === 0 && (
                    <p className="text-muted-foreground text-center py-4">No sections added</p>
                  )}
                </div>
                
                <div className="mt-4 pt-4 border-t border-gray-200 space-y-2">
                  <div className="flex justify-between">
                    <span>Subtotal:</span>
                    <span>${subtotal.toFixed(2)}</span>
                  </div>
                  {taxAmount > 0 && (
                    <div className="flex justify-between">
                      <span>Tax ({formData.tax_rate}%):</span>
                      <span>${taxAmount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-xl font-bold">
                    <span>Total:</span>
                    <span className="text-primary">${total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}
            
            {/* ITEMIZED/DETAILED MODE - Line Items */}
            {formData.display_mode === 'detailed' && (
              <div className="card-default">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-lg">Line Items</h3>
                  <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700">Itemized</span>
                </div>
                <div className="space-y-3">
                  {formData.line_items.filter(item => item.name).map((item, index) => (
                    <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                      <div>
                        <p className="font-medium">{item.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {item.qty} x ${item.rate} / {item.unit}
                        </p>
                      </div>
                      <span className="font-bold">${(item.qty * item.rate).toFixed(2)}</span>
                    </div>
                  ))}
                  {formData.line_items.filter(item => item.name).length === 0 && (
                    <p className="text-muted-foreground text-center py-4">No line items added</p>
                  )}
                </div>
                
                <div className="mt-4 pt-4 border-t border-gray-200 space-y-2">
                  <div className="flex justify-between">
                    <span>Subtotal:</span>
                    <span>${subtotal.toFixed(2)}</span>
                  </div>
                  {taxAmount > 0 && (
                    <div className="flex justify-between">
                      <span>Tax ({formData.tax_rate}%):</span>
                      <span>${taxAmount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-xl font-bold">
                    <span>Total:</span>
                    <span className="text-primary">${total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}
            
            {/* SECTIONS/COMBINED MODE - Multiple Sections */}
            {formData.display_mode === 'sections' && (
              <div className="card-default">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-lg">Estimate Sections</h3>
                  <span className="text-xs px-2 py-1 rounded-full bg-orange-100 text-orange-700">Combined</span>
                </div>
                <div className="space-y-4">
                  {(formData.estimate_sections || []).filter(s => s.title).map((section, index) => (
                    <div key={index} className="border border-gray-200 rounded-xl overflow-hidden">
                      <div className="bg-primary p-3 text-white">
                        <span className="font-semibold">{section.title}</span>
                      </div>
                      <div className="p-3">
                        {section.line_items?.filter(item => item.name).map((item, i) => (
                          <div key={i} className="flex justify-between py-1 text-sm">
                            <span>{item.name}</span>
                            <span>${(item.qty * item.rate).toFixed(2)}</span>
                          </div>
                        ))}
                        <div className="mt-2 pt-2 border-t flex justify-between font-medium">
                          <span>Section Total:</span>
                          <span>${(section.subtotal || 0).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="mt-4 pt-4 border-t border-gray-200 space-y-2">
                  <div className="flex justify-between">
                    <span>Subtotal:</span>
                    <span>${subtotal.toFixed(2)}</span>
                  </div>
                  {taxAmount > 0 && (
                    <div className="flex justify-between">
                      <span>Tax ({formData.tax_rate}%):</span>
                      <span>${taxAmount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-xl font-bold">
                    <span>Total:</span>
                    <span className="text-primary">${total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}
            
            {/* Preview Button */}
            <Button
              variant="outline"
              onClick={() => setShowPreview(true)}
              className="w-full h-12 text-lg"
              data-testid="preview-estimate-btn"
            >
              <Eye className="w-5 h-5 mr-2" />
              Preview as Client
            </Button>
            
            {/* Internal Notes */}
            <div>
              <Label className="text-lg">Internal Notes (not visible to client)</Label>
              <textarea
                value={formData.internal_notes}
                onChange={(e) => updateForm('internal_notes', e.target.value)}
                placeholder="Any notes for your team..."
                className="input-default mt-2 w-full min-h-[80px] py-3"
                data-testid="internal-notes-input"
              />
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="max-w-2xl mx-auto pb-32 md:pb-8" data-testid="estimate-wizard-page">
      {/* Template Selector Dialog */}
      <EstimateTemplateSelector
        isOpen={showTemplateSelector}
        onClose={() => setShowTemplateSelector(false)}
        onSelectTemplate={handleSelectTemplate}
        onStartBlank={() => setShowTemplateSelector(false)}
        enabledModes={organization?.enabled_estimate_modes || ['simple', 'detailed']}
      />

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/estimates')}
            className="p-2"
          >
            <ArrowLeft className="w-6 h-6" />
          </Button>
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold">
              {isEditMode ? 'Edit Estimate' : 'New Estimate'}
            </h1>
            {selectedTemplate && (
              <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                <Sparkles className="w-3 h-3" />
                Using template: {selectedTemplate.name}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Quick Toggle for Line Items */}
          {orgLineItemsMode !== 'required' && (
            <button
              onClick={handleQuickToggleLineItems}
              className={`hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                lineItemsMode === 'hidden'
                  ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                  : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
              }`}
              title={lineItemsMode === 'hidden' ? 'Click to show pricing step' : 'Click to skip pricing step'}
              data-testid="quick-toggle-line-items"
            >
              <DollarSign className="w-3.5 h-3.5" />
              {lineItemsMode === 'hidden' ? 'Pricing: Skip' : 'Pricing: On'}
            </button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowTemplateSelector(true)}
            className="hidden sm:flex"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Templates
          </Button>
        </div>
      </div>
      
      {/* Progress */}
      <div className="mb-8 overflow-x-auto">
        <div className="flex items-center min-w-max">
          {STEPS.map((s, index) => {
            const Icon = s.icon;
            const isActive = step === s.id;
            const isCompleted = step > s.id;
            
            return (
              <div key={s.id} className={`flex items-center ${index < STEPS.length - 1 ? 'flex-1' : ''}`}>
                <button
                  onClick={() => isCompleted && setStep(s.id)}
                  disabled={!isCompleted}
                  className={`flex flex-col items-center ${isCompleted ? 'cursor-pointer' : 'cursor-default'}`}
                >
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
                      isActive
                        ? 'bg-primary text-white'
                        : isCompleted
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {isCompleted ? <Check className="w-6 h-6" /> : <Icon className="w-6 h-6" />}
                  </div>
                  <span className={`text-xs mt-1 font-medium ${isActive ? 'text-primary' : 'text-muted-foreground'}`}>
                    {s.title}
                  </span>
                </button>
                {index < STEPS.length - 1 && (
                  <div
                    className={`flex-1 h-1 mx-2 rounded ${
                      step > s.id ? 'bg-green-500' : 'bg-gray-200'
                    }`}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Content */}
      <div className="card-default">
        {renderStepContent()}
        
        {/* Navigation */}
        <div className="flex flex-col sm:flex-row sm:justify-between gap-3 mt-8 pt-6 border-t border-gray-100">
          <div className="flex items-center gap-2 order-2 sm:order-1">
            {step > 1 && (
              <Button
                variant="outline"
                onClick={handleBack}
                className="h-12 sm:h-14 px-4 sm:px-6 flex-1 sm:flex-initial"
                data-testid="wizard-back-btn"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={handleSaveDraft}
              disabled={isSaving || !formData.client_id || !formData.service_type}
              className="h-12 sm:h-14 px-4 sm:px-6 flex-1 sm:flex-initial"
              data-testid="save-draft-btn"
            >
              <Save className="w-5 h-5 mr-2" />
              Save Draft
            </Button>
          </div>
          
          {step < maxStep ? (
            <Button
              onClick={handleNext}
              className="btn-primary h-12 sm:h-14 order-1 sm:order-2"
              data-testid="wizard-next-btn"
            >
              Next
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          ) : (
            <div className="flex items-center gap-2 order-1 sm:order-2">
              {/* Save as Template Button - only show in summary mode on final step */}
              {formData.display_mode === 'summary' && (
                <Button
                  variant="outline"
                  onClick={() => setShowSaveTemplateModal(true)}
                  disabled={isSaving || formData.summary_sections?.filter(s => s.title.trim()).length === 0}
                  className="h-12 sm:h-14 px-3 sm:px-4"
                  data-testid="save-as-template-btn"
                >
                  <Star className="w-4 h-4 mr-1" />
                  <span className="hidden sm:inline">Save as Template</span>
                </Button>
              )}
              <Button
                onClick={handleCreate}
                disabled={isSaving}
                className="btn-secondary h-12 sm:h-14 flex-1 sm:flex-initial"
                data-testid="create-estimate-btn"
              >
                {isSaving ? (
                  <span className="flex items-center gap-2">
                    <span className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                    {isEditMode ? 'Saving...' : 'Creating...'}
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Check className="w-5 h-5" />
                    {isEditMode ? 'Save Changes' : 'Create Estimate'}
                  </span>
                )}
              </Button>
            </div>
          )}
        </div>
      </div>
      
      {/* Quick Field Notes Drawer (still accessible programmatically if needed) */}
      <QuickFieldNotes
        isOpen={showQuickNotes}
        onClose={() => setShowQuickNotes(false)}
        onSave={setQuickNotes}
        initialData={quickNotes}
      />

      {/* Client Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-primary to-primary/80 p-4 text-white flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <Eye className="w-6 h-6" />
                <div>
                  <h2 className="text-lg font-bold">Client Preview</h2>
                  <p className="text-sm text-white/80">This is how your client will see the estimate</p>
                </div>
              </div>
              <button
                onClick={() => setShowPreview(false)}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body - Scrollable */}
            <div className="p-6 overflow-y-auto flex-1 bg-gray-50">
              {/* Company Header */}
              <div className="text-center mb-6 pb-6 border-b border-gray-200">
                <h1 className="text-2xl font-bold text-gray-900">{organization?.company_name || 'Your Company'}</h1>
                <p className="text-gray-500 text-sm mt-1">ESTIMATE</p>
              </div>

              {/* Client Info */}
              <div className="bg-white rounded-xl p-4 mb-4 shadow-sm">
                <p className="text-sm text-gray-500">Prepared For</p>
                <p className="font-semibold text-lg">{selectedClient?.name}</p>
                {selectedClient?.email && <p className="text-gray-600 text-sm">{selectedClient.email}</p>}
              </div>

              {/* Service Type */}
              <div className="bg-white rounded-xl p-4 mb-4 shadow-sm">
                <p className="text-sm text-gray-500">Service</p>
                <p className="font-semibold">{formData.service_type}</p>
                {formData.job_address && (
                  <p className="text-gray-600 text-sm mt-1">📍 {formData.job_address}</p>
                )}
              </div>

              {/* Pricing - Different modes */}
              <div className="bg-white rounded-xl overflow-hidden shadow-sm mb-4">
                <div className="bg-primary text-white p-3 font-semibold">
                  Estimate Details
                </div>
                
                {/* SIMPLE MODE Preview */}
                {formData.display_mode === 'simple' && (
                  <div>
                    {/* Scope of Work */}
                    {(formData.simple_materials || formData.simple_process) && (
                      <div className="p-4 border-b border-gray-100">
                        <h4 className="font-semibold text-gray-900 mb-3">Scope of Work</h4>
                        {formData.simple_materials && (
                          <div className="mb-3">
                            <p className="text-xs font-medium text-gray-500 uppercase mb-1">Materials</p>
                            <p className="text-gray-700 text-sm whitespace-pre-line">{formData.simple_materials}</p>
                          </div>
                        )}
                        {formData.simple_process && (
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase mb-1">Process</p>
                            <p className="text-gray-700 text-sm whitespace-pre-line">{formData.simple_process}</p>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Work Items */}
                    <div className="divide-y divide-gray-100">
                      {(formData.simple_pricing || []).filter(item => item.title).map((item, index) => (
                        <div key={index} className="p-4 flex justify-between items-center">
                          <span className="font-medium text-gray-900">{item.title}</span>
                          <span className="font-bold text-primary">${(item.price || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                        </div>
                      ))}
                      {(formData.simple_pricing || []).filter(item => item.title).length === 0 && (
                        <div className="p-8 text-center text-gray-400">
                          No work items added yet
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Summary Mode Preview */}
                {formData.display_mode === 'summary' && (
                  <div className="divide-y divide-gray-100">
                    {(formData.summary_sections || []).filter(s => s.title).map((section, index) => (
                      <div key={index} className="p-4">
                        <h4 className="font-semibold text-gray-900 mb-2">{section.title}</h4>
                        {section.description && (
                          <p className="text-gray-600 text-sm whitespace-pre-line">{section.description}</p>
                        )}
                      </div>
                    ))}
                    {(formData.summary_sections || []).filter(s => s.title).length === 0 && (
                      <div className="p-8 text-center text-gray-400">
                        No sections added yet
                      </div>
                    )}
                  </div>
                )}

                {/* Itemized/Detailed Mode Preview */}
                {formData.display_mode === 'detailed' && (
                  <div className="divide-y divide-gray-100">
                    {(formData.line_items || []).filter(item => item.name).map((item, index) => (
                      <div key={index} className="p-4 flex justify-between items-center">
                        <div>
                          <p className="font-medium text-gray-900">{item.name}</p>
                          <p className="text-sm text-gray-500">
                            {item.qty} {item.unit} × ${parseFloat(item.rate).toFixed(2)}
                          </p>
                        </div>
                        <span className="font-bold">${(item.qty * item.rate).toFixed(2)}</span>
                      </div>
                    ))}
                    {(formData.line_items || []).filter(item => item.name).length === 0 && (
                      <div className="p-8 text-center text-gray-400">
                        No items added yet
                      </div>
                    )}
                  </div>
                )}
                
                {/* Sections/Combined Mode Preview */}
                {formData.display_mode === 'sections' && (
                  <div className="divide-y divide-gray-100">
                    {(formData.estimate_sections || []).filter(s => s.title).map((section, index) => (
                      <div key={index} className="p-4">
                        <h4 className="font-semibold text-gray-900 mb-2">{section.title}</h4>
                        {section.line_items?.filter(item => item.name).map((item, i) => (
                          <div key={i} className="flex justify-between text-sm py-1">
                            <span className="text-gray-600">{item.name}</span>
                            <span>${(item.qty * item.rate).toFixed(2)}</span>
                          </div>
                        ))}
                        <div className="flex justify-between font-medium mt-2 pt-2 border-t border-gray-100">
                          <span>Section Total</span>
                          <span>${(section.subtotal || 0).toFixed(2)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Totals */}
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <div className="flex justify-between py-2">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-medium">${subtotal.toFixed(2)}</span>
                </div>
                {taxAmount > 0 && (
                  <div className="flex justify-between py-2 border-t border-gray-100">
                    <span className="text-gray-600">Tax ({formData.tax_rate}%)</span>
                    <span>${taxAmount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between py-3 border-t-2 border-gray-200 text-xl font-bold">
                  <span>Total</span>
                  <span className="text-primary">${total.toFixed(2)}</span>
                </div>
                {formData.require_deposit && depositAmount > 0 && (
                  <div className="mt-3 p-3 bg-primary/10 rounded-lg">
                    <div className="flex justify-between text-sm">
                      <span className="text-primary font-medium">Deposit Required ({formData.deposit_value}%)</span>
                      <span className="font-bold text-primary">${depositAmount.toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 bg-white border-t flex-shrink-0">
              <Button
                onClick={() => setShowPreview(false)}
                className="w-full h-12"
              >
                Close Preview
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Save as Template Modal */}
      <Dialog open={showSaveTemplateModal} onOpenChange={setShowSaveTemplateModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-500" />
              Save as Template
            </DialogTitle>
            <p className="text-sm text-muted-foreground">
              Save this estimate format as a reusable template for future estimates.
            </p>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="template-name">Template Name *</Label>
              <Input
                id="template-name"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="e.g., Basic Driveway Repair"
                className="mt-1"
                data-testid="template-name-input"
              />
            </div>
            
            <div>
              <Label htmlFor="template-desc">Description (optional)</Label>
              <Textarea
                id="template-desc"
                value={templateDescription}
                onChange={(e) => setTemplateDescription(e.target.value)}
                placeholder="When to use this template..."
                className="mt-1 h-20"
                data-testid="template-description-input"
              />
            </div>
            
            <div className="bg-gray-50 rounded-lg p-3 space-y-2 text-sm">
              <p className="font-medium text-gray-700">Template will include:</p>
              <ul className="text-gray-600 space-y-1">
                <li>• Service type: {formData.service_type || 'Not set'}</li>
                <li>• {formData.summary_sections?.filter(s => s.title.trim()).length || 0} section(s)</li>
                <li>• Tax rate: {formData.tax_rate || 0}%</li>
                {formData.require_deposit && (
                  <li>• Deposit: {formData.deposit_value}% ({formData.deposit_type})</li>
                )}
              </ul>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowSaveTemplateModal(false)}
              disabled={isSavingTemplate}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveAsTemplate}
              disabled={isSavingTemplate || !templateName.trim()}
              data-testid="save-template-confirm-btn"
            >
              {isSavingTemplate ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </span>
              ) : (
                'Save Template'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EstimateWizardPage;
